import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle'
import { ProfileSelectOption } from 'components/ui/forms/course/components/forms/RequiredProfilesInput'
import { lmsProfilesService } from 'api/lms-profiles'
import { CourseProfilesCard } from 'components/reusables/courseProfilesField/CourseProfilesCard'
import styles from 'components/reusables/courseProfilesField/styles.module.scss'

interface IProps {
  profiles: ProfileSelectOption[]
  onChange: (profiles: ProfileSelectOption[]) => void
  learningPathId?: number
  disabled?: boolean
}

const translationPrefix =
  'administration.learningPaths.manage.create.configuration.inputs.profiles.profilesField'

/**
 * Selector de perfiles LMS para programas de aprendizaje (misma UX que cursos).
 */
export const LearningPathProfilesField = ({
  profiles,
  onChange,
  learningPathId,
  disabled = false,
}: IProps) => {
  const { t } = useTranslation()
  const [restrictedProfilesCount, setRestrictedProfilesCount] = useState(0)

  const fetchRestrictedCount = useCallback(async () => {
    if (!learningPathId) return 0

    const { data } = await lmsProfilesService.getProfilesByResource(
      { id: learningPathId, type: 'learning_path' },
      { page: 1, search: '', perPage: 1 }
    )

    return data.restrictedProfilesCount || 0
  }, [learningPathId])

  useEffect(() => {
    if (!learningPathId) {
      setRestrictedProfilesCount(0)
      return
    }

    let cancelled = false

    fetchRestrictedCount().then((count) => {
      if (!cancelled) setRestrictedProfilesCount(count)
    })

    return () => {
      cancelled = true
    }
  }, [fetchRestrictedCount, learningPathId])

  return (
    <div className={styles.wrapper}>
      {restrictedProfilesCount > 0 && (
        <div className={styles.restrictedAlert}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <p>{t(`${translationPrefix}.restrictedAlert`)}</p>
        </div>
      )}

      <CourseProfilesCard
        profiles={profiles}
        excludedProfileIds={new Set()}
        restrictedProfilesCount={restrictedProfilesCount}
        onChange={onChange}
        disabled={disabled}
        showLabel={false}
        translationPrefix={translationPrefix}
      />
    </div>
  )
}

export default LearningPathProfilesField
